import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  blur?: boolean;
  quality?: number;
}

const ImageContainer = styled.div<{ $isLoading: boolean }>`
  position: relative;
  overflow: hidden;
  background-color: var(--color-background-secondary);
  transition: filter 0.3s ease;
  filter: ${props => props.$isLoading ? 'blur(10px)' : 'none'};
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--color-background-secondary);
`;

export const OptimizedImage: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  blur = true,
  quality = 75,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageSrc, setImageSrc] = useState<string>('');

  useEffect(() => {
    const img = new Image();
    
    // Construire l'URL optimisée
    const optimizedSrc = src.startsWith('http') 
      ? new URL(src)
      : new URL(src, window.location.origin);
    
    // Ajouter les paramètres d'optimisation
    if (width) optimizedSrc.searchParams.append('w', width.toString());
    if (height) optimizedSrc.searchParams.append('h', height.toString());
    if (quality) optimizedSrc.searchParams.append('q', quality.toString());
    
    img.src = optimizedSrc.toString();
    img.onload = () => {
      setImageSrc(optimizedSrc.toString());
      setIsLoading(false);
    };
  }, [src, width, height, quality]);

  return (
    <ImageContainer $isLoading={isLoading && blur}>
      {isLoading && <Placeholder />}
      <StyledImage
        src={imageSrc || src}
        alt={alt}
        width={width}
        height={height}
        loading="lazy"
        decoding="async"
        {...props}
      />
    </ImageContainer>
  );
};
